import { Center, Checkbox, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import {
  AssigningParentInterface,
  ConsumerCandidateInterface,
  PotentialDuplicateCandidateInterface
} from '../../../../../shared/type/consumers-candidates.type';
import IconComponent from '../../../../IconComponent/IconComponent';
import CustomChakraButton from '../../../../CustomChakraButton/CustomChakraButton';
import { useApi } from '../../../../../context/ApiProvider';
import { useAlertContext } from '../../../../../context/AlertProvider';
import { AxiosResponse, HttpStatusCode } from 'axios';

type PendingChildrenTableProps = {
  isLoading: boolean;
  pendingChildren?: ConsumerCandidateInterface[];
  areAllSelected: boolean;
  toggleSingleKid: (kidId: number, checked: boolean) => void;
  toggleSelectAll: (checked: boolean) => void;
  selectedKidIds: number[];
  afterAction: () => Promise<void>;
};

export const PendingChildrenTable = ({
  isLoading,
  pendingChildren,
  areAllSelected,
  toggleSingleKid,
  toggleSelectAll,
  selectedKidIds,
  afterAction
}: PendingChildrenTableProps) => {
  return (
    <Table className="w-full">
      <Thead className="text-xs bg-white p-1 mb-4">
        <Tr>
          <Th
            className="w-[50px] border-b rounded-lg rounded-r-none border-gray-200 p-1"
            textTransform="capitalize"
            fontWeight={400}>
            <Checkbox
              colorScheme="customOrange"
              isChecked={areAllSelected}
              onChange={(e) => toggleSelectAll(e.target.checked)}
            />
          </Th>
          <Th
            className="w-[75px] border-b rounded-lg rounded-r-none border-gray-200 p-1"
            textTransform="capitalize"
            fontWeight={400}>
            Lp.
          </Th>
          <Th
            className="w-[200px] border-b border-gray-200 py-4 px-2"
            textTransform="capitalize"
            fontWeight={400}>
            Imię
          </Th>
          <Th
            className="w-[200px] border-b border-gray-200 py-4 px-2"
            textTransform="capitalize"
            fontWeight={400}>
            Nazwisko
          </Th>
          <Th
            className="w-[75px] border-b border-gray-200 py-4 px-2"
            textTransform="capitalize"
            fontWeight={400}>
            Klasa
          </Th>
          <Th
            className="w-[200px] border-b rounded-lg border-gray-200 py-4 px-2"
            textTransform="capitalize"
            fontWeight={400}>
            Rodzic zgłaszający
          </Th>
          <Th
            className="border border-l-0 rounded-lg  border-gray-200 py-4 px-2"
            textTransform="capitalize"
            fontWeight={400}>
            Komunikat
          </Th>
        </Tr>
      </Thead>
      <Tbody className="text-sm font-medium text-grayLight-900">
        {isLoading ? (
          <Tr>
            <Td colSpan={7}>
              <Center>
                <Spinner size="xl" />
              </Center>
            </Td>
          </Tr>
        ) : pendingChildren && pendingChildren.length === 0 ? (
          <Tr>
            <Td colSpan={7}>
              <Center>
                <Text>Brak danych</Text>
              </Center>
            </Td>
          </Tr>
        ) : (
          pendingChildren &&
          pendingChildren.map((child, index) => {
            return (
              <Tr key={child.candidateData.id} className={`border-t border-x bg-white`}>
                <Td className="px-6 py-5 text-grayLight-600" textTransform="capitalize">
                  <Checkbox
                    colorScheme="customOrange"
                    isChecked={selectedKidIds.includes(child.candidateData.id)}
                    onChange={(e) => toggleSingleKid(child.candidateData.id, e.target.checked)}
                  />
                </Td>
                <Td className="px-6 py-5 text-grayLight-600" textTransform="capitalize">
                  {index + 1}
                </Td>
                <Td className="px-6 py-5 text-grayLight-600" textTransform="capitalize">
                  {child.candidateData.firstName}
                </Td>
                <Td className="px-6 py-5 text-grayLight-600" textTransform="capitalize">
                  {child.candidateData.lastName}
                </Td>
                <Td className="px-6 py-5 text-grayLight-600" textTransform="capitalize">
                  {child.candidateData.organisationUnitName}
                </Td>
                <Td className="px-6 py-5 text-grayLight-600" textTransform="capitalize">
                  {child.owner.firstName} {child.owner.lastName}
                </Td>
                <Td className="px-6 py-5 text-grayLight-600">
                  {child.potentialDuplicates.map((duplicate) => (
                    <DuplicatedCandidatesColumn
                      consumerCandidateId={child.candidateData.id}
                      duplicateData={duplicate}
                      assingingParent={child.owner}
                      afterAction={afterAction}
                    />
                  ))}
                </Td>
              </Tr>
            );
          })
        )}
        <Tr className={`border-t border-x bg-white`}>
          <Td className="px-6 py-8 h-14 text-grayLight-600" colSpan={5}></Td>
        </Tr>
      </Tbody>
    </Table>
  );
};

type DuplicatedCandidatesColumnProps = {
  consumerCandidateId: number;
  duplicateData: PotentialDuplicateCandidateInterface;
  assingingParent: AssigningParentInterface;
  afterAction: () => Promise<void>;
};

export const DuplicatedCandidatesColumn = ({
  consumerCandidateId,
  duplicateData,
  assingingParent,
  afterAction
}: DuplicatedCandidatesColumnProps) => {
  const { apiTenantConsumerCandidateController } = useApi();
  const { setShow, setAlertProperties } = useAlertContext();

  const handleMergeConsuments = async () => {
    if (consumerCandidateId === undefined) {
      return;
    }

    try {
      const response: AxiosResponse<ConsumerCandidateInterface[]> =
        await apiTenantConsumerCandidateController('merge').post('', {
          consumerCandidateId,
          consumerId: duplicateData.consumer.id
        });

      if (response.status === HttpStatusCode.Ok) {
        setAlertProperties({
          timeout: 9000,
          title: 'Uczeń zostało przypisany do zgłaszającego rodzica',
          status: 'success'
        });
        afterAction();
      }
    } catch (error) {
      console.error('Failed to merge consumer candidates', error);
      setAlertProperties({
        timeout: 9000,
        title: 'Uczeń nie został przypisany do zgłaszającego rodzica',
        status: 'error'
      });
    } finally {
      setShow(true);
    }
  };

  return (
    <div className="flex gap-3 items-start">
      <IconComponent className="w-5 h-5" iconName="alertTriangle" />
      <div className="max-w-[440px]">
        <p className="text-sm font-semibold text-grayLight-900 text-ellipsis text-wrap">
          Konto {duplicateData.consumer.firstName} {duplicateData.consumer.lastName}{' '}
          {duplicateData.consumer.organisationUnitName} już istnieje w systemie. Przypisani rodzice:{' '}
          {duplicateData.parents
            .map((parent) => `${parent.firstName} ${parent.lastName}`)
            .join(',')}
          .
        </p>
        <p className="text-sm font-normal text-grayLight-700 text-ellipsis text-wrap">
          Przypisz zgłaszającego rodzica {assingingParent.firstName} {assingingParent.lastName} do
          istniejącego ucznia {duplicateData.consumer.firstName} {duplicateData.consumer.lastName}
        </p>
        <div className="mt-2">
          <CustomChakraButton
            hierarchy="secondaryColor"
            size="md"
            iconPosition="left"
            icon="userPlus"
            buttonProps={{
              onClick: () => {
                handleMergeConsuments();
              }
            }}>
            Przypisz konto
          </CustomChakraButton>
        </div>
      </div>
    </div>
  );
};
